import Anchor from "atoms/anchors/anchor";
import Button from "atoms/buttons/button";
import PublicationComponentType from "utilities/enumerations/publication-component-type";
import React, { PropsWithChildren, useLayoutEffect, useRef } from "react";
import StringUtils from "utilities/string-utils";
import TableOfContentsBookmarkIndicator from "organisms/table-of-contents/table-of-contents-bookmark-indicator";
import { ButtonStyles } from "atoms/constants/button-styles";
import { DataTestAttributes } from "interfaces/data-test-attributes";

interface TableOfContentsCollapsePanelLinkProps
    extends Pick<
        DataTestAttributes,
        "dataTestDisplaySequence" | "dataTestExternalId" | "dataTestId"
    > {
    hasBookmark?: boolean;
    index?: number | string;
    isPublic?: boolean;
    onClick?: () => void;
    to?: string;
    useRelativePath?: boolean;
    caapsId?: string | number;
    publicationComponentType?: PublicationComponentType;
    isActive?: boolean;
    isPdfPublication?: boolean;
    autoScroll?: boolean;
}

const TableOfContentsCollapsePanelLink: React.FunctionComponent<
    PropsWithChildren<TableOfContentsCollapsePanelLinkProps>
> = (props) => {
    const {
        children,
        dataTestDisplaySequence,
        dataTestExternalId,
        dataTestId,
        hasBookmark = false,
        onClick,
        index,
        isPublic = false,
        to,
        useRelativePath = false,
        isPdfPublication,
        autoScroll = true,
    } = props;
    const cssBaseClass = "c-table-of-contents-section__item";
    const cssPublicModifier = "-public";
    const cssClassName = isPublic
        ? StringUtils.joinClassNames([cssBaseClass, cssPublicModifier])
        : cssBaseClass;

    const dataTestProps = {
        "data-test-display-sequence": dataTestDisplaySequence,
        "data-test-external-id": dataTestExternalId,
        "data-test-id": dataTestId,
    };

    const isActiveRef = useRef<HTMLLIElement>(null);

    useLayoutEffect(() => {
        requestAnimationFrame(() => {
            if (isActiveRef.current && autoScroll) {
                isActiveRef.current.scrollIntoView({
                    behavior: "instant",
                    block: "center",
                });
            }
        });
    }, [autoScroll]);

    if (isPublic) {
        return (
            <li key={index}>
                <div className={cssClassName} {...dataTestProps}>
                    {children}
                    <TableOfContentsBookmarkIndicator
                        hasBookmark={hasBookmark}
                    />
                </div>
            </li>
        );
    }

    return (
        <li
            ref={props.isActive ? isActiveRef : null}
            key={index}
            onClick={onClick}
            className={`c-table-of-contents-section__container ${props.isActive ? "-active" : ""}`}
            {...dataTestProps}>
            {isPdfPublication ? (
                <Button
                    accessibleText={children?.toString()}
                    cssClassName={` ${cssClassName} ${props.isActive ? "-active" : ""}`}
                    onClick={onClick}
                    style={ButtonStyles.None}>
                    {children}
                </Button>
            ) : (
                <Anchor
                    cssClassName={` ${cssClassName} ${props.isActive ? "-active" : ""}`}
                    dataTestDisplaySequence={dataTestDisplaySequence}
                    dataTestExternalId={dataTestExternalId}
                    dataTestId={dataTestId}
                    to={to ?? ""}
                    useRelativePath={useRelativePath}>
                    {children}
                    <TableOfContentsBookmarkIndicator
                        hasBookmark={hasBookmark}
                    />
                </Anchor>
            )}
        </li>
    );
};

export default TableOfContentsCollapsePanelLink;
